import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { ORDER_SUPPLIER } from "../../constants/routes";
import { ORDER_FULFILLMENT_TYPES } from "../../constants/sellarConstants.shared";
import { useOrderInvoiceUrl } from "../../hooks/api/v2/order/useOrderInvoiceUrl";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { OrderCollectedDialog } from "../../pages/order/modals/OrderCollectedDialog";
import { OrderDeliveredDialog } from "../../pages/order/modals/OrderDeliveredDialog";
import { OrderDispatchedDialog } from "../../pages/order/modals/OrderDispatchedDialog";
import { BuyerOnly } from "../authFilter/BuyerOnly";
import { SupplierOnly } from "../authFilter/SupplierOnly";
import { Button, Icon } from "@sellar-io/components";

export const OrderSummaryHeaderActionButtons = ({
  cancelledAt,
  cancelledBy,
  voidedAt,
  confirmedAt,
  deliveredAt,
  deliveryPlannedAt,
  dispatchedAt,
  invoiceFinalizedAt,
  fulfillmentType,
  orderId,
  paymentStatus,
  paymentDue,
  paymentType,
  retailerId,
  stripeInvoiceId,
  orderItems,
  connectionId,
  invoiceCreationStage,
  invoiceIssuedAt,
}: {
  cancelledAt: Date | null;
  cancelledBy: OrderGetResponseType["cancelledBy"];
  voidedAt: Date | null;
  confirmedAt: Date | null;
  deliveredAt: Date | null;
  deliveryPlannedAt: Date | null;
  dispatchedAt: Date | null;
  invoiceFinalizedAt: Date | null;
  fulfillmentType?: OrderGetResponseType["fulfillmentType"];
  orderId: number;
  paymentStatus: OrderGetResponseType["paymentStatus"] | null;
  paymentDue: Date | null;
  paymentType: OrderGetResponseType["paymentType"];
  retailerId: number;
  stripeInvoiceId?: string | null;
  orderItems: { ProductId: number; quantity: number }[];
  connectionId?: number | string;
  invoiceCreationStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  invoiceIssuedAt: Date | null;
}): ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const [orderDeliveredDialogActive, setOrderDeliveredDialogActive] =
    useState(false);
  const [orderCollectedDialogActive, setOrderCollectedDialogActive] =
    useState(false);
  const [orderDispatchedDialogActive, setOrderDispatchedDialogActive] =
    useState(false);

  const invoiceUrlQuery = useOrderInvoiceUrl(orderId, {
    enabled:
      !!(
        invoiceFinalizedAt ||
        paymentStatus === "open" ||
        paymentStatus === "paid"
      ) &&
      !!business?.Retailer &&
      !!stripeInvoiceId,
  });
  const navigate = useNavigate();
  const invoiceSent =
    !!invoiceFinalizedAt || (!!invoiceIssuedAt && paymentType === "manual");
  const invoicePaid = paymentStatus === "paid";

  const canDisplayCollectedButton =
    fulfillmentType === ORDER_FULFILLMENT_TYPES.COLLECTION.value &&
    confirmedAt &&
    !deliveredAt &&
    !cancelledAt;

  return (
    <>
      <SupplierOnly>
        {!voidedAt &&
          !cancelledBy &&
          confirmedAt &&
          !dispatchedAt &&
          !deliveredAt &&
          fulfillmentType !== ORDER_FULFILLMENT_TYPES.COLLECTION.value &&
          fulfillmentType !== ORDER_FULFILLMENT_TYPES.DISTRIBUTION.value && (
            <Button
              className={"w-full"}
              onClick={() => setOrderDispatchedDialogActive(true)}
              color={
                dayjs().diff(dayjs(paymentDue), "days") >= 1 ||
                (paymentType === "stripe" && !invoiceFinalizedAt)
                  ? "secondary"
                  : "primary"
              }
            >
              {" "}
              <Icon size={0.7} icon={"DELIVERY_BOX"} /> Dispatched
            </Button>
          )}
        {!voidedAt &&
          !cancelledBy &&
          confirmedAt &&
          dispatchedAt &&
          !deliveredAt &&
          fulfillmentType !== ORDER_FULFILLMENT_TYPES.COLLECTION.value &&
          fulfillmentType !== ORDER_FULFILLMENT_TYPES.DISTRIBUTION.value && (
            <Button
              className={"w-full"}
              onClick={() => setOrderDeliveredDialogActive(true)}
              color={
                dayjs().diff(dayjs(paymentDue), "days") >= 1 ||
                (paymentType === "stripe" && !invoiceFinalizedAt)
                  ? "secondary"
                  : "primary"
              }
            >
              <Icon size={0.7} icon={"CHECK_ALL"} /> Delivered
            </Button>
          )}
        {!voidedAt && canDisplayCollectedButton && (
          <Button
            className={"w-full"}
            onClick={() => setOrderCollectedDialogActive(true)}
          >
            <Icon size={0.7} icon={"CHECK_ALL"} /> Collected
          </Button>
        )}
        {!voidedAt && cancelledAt && connectionId && (
          <Button
            color={"secondary"}
            onClick={() =>
              navigate(ORDER_SUPPLIER.href({ connectionId: connectionId }), {
                state: { initialOrderItems: orderItems },
              })
            }
          >
            <Icon size={0.7} icon={"AUTORENEW"} /> Recreate Order
          </Button>
        )}
      </SupplierOnly>

      <BuyerOnly>
        {paymentType === "stripe" &&
          paymentStatus === "paid" &&
          invoiceFinalizedAt &&
          stripeInvoiceId &&
          (invoiceUrlQuery.data?.invoiceUrl ||
            invoiceUrlQuery.data?.invoicePdf) && (
            <Button
              className={"w-full"}
              onClick={() =>
                window.open(
                  invoiceUrlQuery.data.invoicePdf ??
                    invoiceUrlQuery.data.invoiceUrl,
                  "_blank",
                )
              }
              color={"secondary"}
            >
              {" "}
              <Icon size={0.7} icon={"OPEN_IN_NEW"} /> VAT Invoice
            </Button>
          )}
      </BuyerOnly>

      <OrderDeliveredDialog
        active={orderDeliveredDialogActive}
        onDismiss={() => setOrderDeliveredDialogActive(false)}
        orderId={orderId}
        retailerId={retailerId}
        invoiceSent={invoiceSent}
        invoicePaid={invoicePaid}
        isInvoiceCreationStage={
          invoiceCreationStage === "delivered" ||
          (invoiceCreationStage === "dispatched" && !dispatchedAt)
        }
        paymentType={paymentType}
      />

      <OrderCollectedDialog
        active={orderCollectedDialogActive}
        onDismiss={() => setOrderCollectedDialogActive(false)}
        orderId={orderId}
        retailerId={retailerId}
        invoiceSent={invoiceSent}
        invoicePaid={invoicePaid}
        isInvoiceCreationStage={
          invoiceCreationStage === "delivered" ||
          (invoiceCreationStage === "dispatched" && !dispatchedAt)
        }
        paymentType={paymentType}
      />
      <OrderDispatchedDialog
        active={orderDispatchedDialogActive}
        onDismiss={() => setOrderDispatchedDialogActive(false)}
        deliveryPlannedAt={deliveryPlannedAt}
        orderId={orderId}
        retailerId={retailerId}
        invoiceSent={invoiceSent}
        invoicePaid={invoicePaid}
        isInvoiceCreationStage={invoiceCreationStage === "dispatched"}
        paymentType={paymentType}
      />
    </>
  );
};
